/**
 * PostHog tracker — thin wrapper around posthog-js with LGPD-safe defaults.
 *
 * Use via `AnalyticsProvider` (which handles init + consent) and the
 * exported helpers below. Do NOT call `posthog.capture` directly — go
 * through `track()` so the event-name type-check and PII guard apply.
 */
import posthog from "posthog-js";
import type {
  AnalyticsConfig,
  AnalyticsEvent,
  EventProperties,
} from "./types";

const DEFAULT_HOST = "https://us.i.posthog.com";

let initialized = false;
let currentConfig: AnalyticsConfig | null = null;

/**
 * Initialize PostHog. Safe to call multiple times — subsequent calls
 * are ignored. Called by `AnalyticsProvider`.
 */
export function initAnalytics(config: AnalyticsConfig): void {
  if (initialized) return;
  if (config.disabled) {
    currentConfig = config;
    return;
  }
  if (typeof window === "undefined") {
    // SSR pass — skip init, will retry on client hydration
    return;
  }

  posthog.init(config.apiKey, {
    api_host: config.apiHost ?? DEFAULT_HOST,
    // Only create person profiles for identified users (cost control).
    person_profiles: "identified_only",
    // Session recording is controlled via PostHog dashboard + per-call
    // start/stop. We default it off here too as a belt-and-suspenders.
    disable_session_recording: true,
    // Autocapture is fine — it picks up clicks on buttons, form submits, etc.
    autocapture: true,
    capture_pageview: false, // we manually capture page_viewed via router
    capture_pageleave: true,
    // Opt-out by default until consent is granted; AnalyticsProvider flips
    // this on once LGPD consent is present.
    opt_out_capturing_by_default: true,
    // Don't persist across domains — each app gets its own distinct_id
    // pool unless we explicitly identify them.
    persistence: "localStorage+cookie",
    loaded: (ph) => {
      ph.register({
        product: config.product,
        environment: config.environment ?? "development",
        platform: config.platform ?? "web",
        ...(config.appVersion ? { app_version: config.appVersion } : {}),
      });
    },
  });

  initialized = true;
  currentConfig = config;
}

/**
 * Grant LGPD consent and start capturing. Call this from your consent
 * banner after the user clicks "Accept".
 */
export function grantConsent(): void {
  if (!initialized) return;
  posthog.opt_in_capturing();
}

/**
 * Revoke LGPD consent and stop capturing.
 */
export function revokeConsent(): void {
  if (!initialized) return;
  posthog.opt_out_capturing();
}

/** True if PostHog is initialized and the user has opted in. */
export function isTracking(): boolean {
  return initialized && !posthog.has_opted_out_capturing();
}

/**
 * Capture a typed event. Use domain-specific helpers (e.g. `trackLeagueViewed`)
 * in each app that wrap this with their own argument shape.
 */
export function track(
  event: AnalyticsEvent,
  properties?: EventProperties,
): void {
  if (!initialized || !currentConfig || currentConfig.disabled) return;
  if (posthog.has_opted_out_capturing()) return;

  // LGPD guard — strip any property that looks like PII before it leaves
  // the process. This is a last-resort safety net; never rely on it as
  // the primary defense.
  const safeProps = stripPii(properties);
  posthog.capture(event, safeProps);
}

/**
 * Identify a user. Call from your auth handler on sign-in with a stable
 * internal ID (e.g. Clerk user id). NEVER pass email, CPF, or phone.
 */
export function identify(
  userId: string,
  traits?: Record<string, unknown>,
): void {
  if (!initialized || !currentConfig || currentConfig.disabled) return;
  if (posthog.has_opted_out_capturing()) return;

  const safeTraits = stripPii(traits as EventProperties | undefined);
  posthog.identify(userId, safeTraits);
}

/** Reset on sign-out — creates a new anonymous distinct_id. */
export function reset(): void {
  if (!initialized) return;
  posthog.reset();
}

/** Read a feature flag value. Returns `false` if flag is off or unknown. */
export function isFeatureEnabled(flag: string): boolean {
  if (!initialized) return false;
  return posthog.isFeatureEnabled(flag) ?? false;
}

/**
 * Expose the underlying posthog client for advanced use cases
 * (e.g. group analytics, session replay control). Prefer the helpers above.
 */
export function getPostHog(): typeof posthog | null {
  return initialized ? posthog : null;
}

// ──────────────────────────────────────────────────────────────────────
// Internal

const PII_KEY_PATTERNS = [
  /email/i,
  /cpf/i,
  /cnpj/i,
  /phone/i,
  /telefone/i,
  /passport/i,
  /password/i,
  /token/i,
  /secret/i,
  /^ip$/i,
  /address/i,
  /birthdate/i,
  /birth_date/i,
  /ssn/i,
];

function stripPii<T extends Record<string, unknown> | undefined>(props: T): T {
  if (!props) return props;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (PII_KEY_PATTERNS.some((pattern) => pattern.test(key))) {
      // Always warn — a PII leak is always a bug, not just a dev-only one.
      // eslint-disable-next-line no-console
      console.warn(
        `[@engagexp/analytics] Dropped PII-looking property "${key}" from event`,
      );
      continue;
    }
    result[key] = value;
  }
  return result as T;
}
